import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Eye, Award, Building2, Scale, Users, GraduationCap, BadgeCheck, Landmark, CheckCircle2 } from 'lucide-react';
const highlights = [{
  icon: Award,
  title: '20+ Years',
  sub: 'Executive Leadership'
}, {
  icon: Building2,
  title: 'CEO',
  sub: 'Utah Medical Association'
}, {
  icon: Scale,
  title: 'Policy Expert',
  sub: 'Healthcare Policy'
}, {
  icon: Landmark,
  title: 'Advocacy Leader',
  sub: 'State & National'
}, {
  icon: Users,
  title: 'Governance',
  sub: 'Board Experience'
}, {
  icon: GraduationCap,
  title: 'MBA',
  sub: 'Summa Cum Laude'
}, {
  icon: BadgeCheck,
  title: 'CAE',
  sub: 'Certified Association Executive'
}];
const metrics = [{
  value: '20+',
  label: 'Years Experience',
  sub: 'Leading healthcare organizations'
}, {
  value: '312%',
  label: 'Revenue Growth',
  sub: 'Foundation & association expansion'
}, {
  value: '45+',
  label: 'Legislative Wins',
  sub: 'State & federal advocacy'
}, {
  value: '7',
  label: 'Board Appointments',
  sub: 'Governance leadership'
}];
const testimonials = [{
  quote: 'Michelle is among the most effective association executives I have worked with — a strategic operator who turns vision into measurable results.',
  name: 'Dr. Andrea Whitlock',
  role: 'Past President, State Medical Association'
}, {
  quote: 'Her command of healthcare policy and ability to build coalitions across the aisle is exceptional. A true transformational leader.',
  name: 'James R. Calloway',
  role: 'Board Chair, Regional Health Network'
}, {
  quote: 'Michelle brings discipline, integrity, and an unwavering focus on outcomes to every board she serves. An ideal director.',
  name: 'Patricia Nguyen',
  role: 'Healthcare Investor & Director'
}];
const summaryPoints = ['Healthcare Leadership', 'Organizational Growth', 'Legislative Advocacy', 'Strategic Partnerships', 'Revenue Expansion', 'Coalition Building'];
export function Home() {
  useEffect(() => {
    document.title = 'Michelle McOmber | Healthcare Executive, Association CEO & Board Leader';
  }, []);
  return <div className="w-full" data-id="element-300">
      {/* Hero */}
      <section className="relative bg-navy-900 text-white overflow-hidden" data-id="element-301">
        <div className="absolute inset-0 opacity-20" data-id="element-302">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80')] bg-cover bg-center mix-blend-overlay" data-id="element-303" />
        </div>
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-32 relative z-10" data-id="element-304">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-305">
            <span className="inline-flex items-center gap-2 bg-gold/15 border border-gold/40 text-gold px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-6" data-id="element-306">
              <span className="w-2 h-2 rounded-full bg-gold" data-id="element-307" />
              Currently Seeking New Leadership Opportunities
            </span>
            <h1 className="text-4xl md:text-6xl font-serif font-bold leading-tight mb-6 text-balance text-white" data-id="element-308">
              Transforming Healthcare Through Leadership, Advocacy &amp;
              Strategic Innovation
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-10 leading-relaxed max-w-2xl" data-id="element-309">
              Healthcare Executive | Association CEO | Board Leader | Healthcare
              Policy Advocate
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-310">
              <Link to="/about" className="bg-gold hover:bg-gold-600 text-white px-8 py-4 text-center font-medium transition-colors flex items-center justify-center group" data-id="element-311">
                View Executive Profile
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-312" />
              </Link>
              <Link to="/contact" className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white px-8 py-4 text-center font-medium transition-colors flex items-center justify-center" data-id="element-313">
                <Calendar className="w-4 h-4 mr-2" data-id="element-314" />
                Schedule a Conversation
              </Link>
              <a href="https://heyzine.com/flip-book/952e7ede0c.html" target="_blank" rel="noopener noreferrer" className="text-white hover:text-gold px-6 py-4 text-center font-medium transition-colors flex items-center justify-center" data-id="element-315">
                <Eye className="w-4 h-4 mr-2" data-id="element-316" />
                View Resume
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-20 bg-white" data-id="element-317">
        <div className="max-w-7xl mx-auto px-6" data-id="element-318">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-8" data-id="element-319">
            {highlights.map(h => <div key={h.title} className="text-center group" data-id="element-320">
                <div className="w-16 h-16 mx-auto bg-slate-50 rounded-full flex items-center justify-center mb-4 group-hover:bg-navy-900 group-hover:text-white transition-colors text-navy-900" data-id="element-321">
                  <h.icon className="w-8 h-8" data-id="element-322" />
                </div>
                <p className="font-serif font-bold text-navy-900 text-lg" data-id="element-323">
                  {h.title}
                </p>
                <p className="text-sm text-slate-500 mt-1" data-id="element-324">{h.sub}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Executive summary */}
      <section className="py-24 bg-slate-50" data-id="element-325">
        <div className="max-w-7xl mx-auto px-6" data-id="element-326">
          <div className="flex flex-col lg:flex-row gap-16 items-center" data-id="element-327">
            <motion.div initial={{
            opacity: 0,
            x: -24
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="lg:w-2/5" data-id="element-328">
              <div className="relative" data-id="element-329">
                <div className="absolute inset-0 bg-gold translate-x-4 translate-y-4" data-id="element-330" />
                <img src="https://cdn.magicpatterns.com/uploads/8aVXY2pBvqpYt8negkm5zj/Michelle_Picture.jpg" alt="Michelle McOmber, healthcare executive" className="relative w-full h-[480px] object-cover object-top" data-id="element-331" />
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 24
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="lg:w-3/5" data-id="element-332">
              <span className="text-gold font-bold tracking-widest uppercase text-sm mb-4 block" data-id="element-333">
                Executive Summary
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy-900 mb-6" data-id="element-334">
                A Proven Healthcare Leader Driving Growth and Transformation
              </h2>
              <div className="space-y-4 text-slate-600 text-lg leading-relaxed mb-8" data-id="element-335">
                <p data-id="element-336">
                  With more than two decades at the helm of healthcare
                  organizations, Michelle McOmber has built a career defined by
                  organizational growth, revenue expansion, and landmark
                  legislative advocacy. As CEO of the Utah Medical Association,
                  she modernized operations, grew membership, and expanded the
                  association&rsquo;s foundation and scholarship programs.
                </p>
                <p data-id="element-337">
                  Her work spans healthcare transformation, coalition building,
                  and strategic partnerships that strengthen the relationship
                  between physicians, policymakers, and the communities they
                  serve.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-y-4 gap-x-8 mb-10" data-id="element-338">
                {summaryPoints.map(p => <div key={p} className="flex items-center text-navy-900 font-medium" data-id="element-339">
                    <span className="w-2 h-2 bg-gold rounded-full mr-3" data-id="element-340" />
                    {p}
                  </div>)}
              </div>
              <Link to="/experience" className="inline-flex items-center text-navy-900 font-bold hover:text-gold transition-colors" data-id="element-341">
                Explore Full Experience
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-342" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Metrics */}
      <section className="py-24 bg-navy-900 text-white" data-id="element-343">
        <div className="max-w-7xl mx-auto px-6" data-id="element-344">
          <div className="text-center mb-16" data-id="element-345">
            <span className="text-gold font-bold tracking-widest uppercase text-sm mb-4 block" data-id="element-346">
              Proven Results
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white" data-id="element-347">
              Leadership Impact Metrics
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-348">
            {metrics.map((m, i) => <motion.div key={m.label} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="text-center" data-id="element-349">
                <div className="text-5xl font-serif font-bold text-gold mb-4" data-id="element-350">
                  {m.value}
                </div>
                <div className="text-xl font-bold mb-2" data-id="element-351">{m.label}</div>
                <div className="text-slate-400 text-sm" data-id="element-352">{m.sub}</div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white" data-id="element-353">
        <div className="max-w-7xl mx-auto px-6" data-id="element-354">
          <div className="text-center mb-16" data-id="element-355">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy-900" data-id="element-356">
              Trusted by Leaders &amp; Recognized
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-357">
            {testimonials.map((t, i) => <motion.div key={t.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="relative bg-slate-50 p-8 rounded-lg" data-id="element-358">
                <span className="text-gold text-6xl font-serif absolute top-6 left-6 opacity-20" data-id="element-359">
                  &ldquo;
                </span>
                <p className="text-slate-700 italic mb-8 relative z-10 pt-4" data-id="element-360">
                  {t.quote}
                </p>
                <div data-id="element-361">
                  <p className="font-bold text-navy-900" data-id="element-362">{t.name}</p>
                  <p className="text-sm text-slate-500" data-id="element-363">{t.role}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-50" data-id="element-364">
        <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-365">
          <CheckCircle2 className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-366" />
          <h2 className="text-3xl font-serif font-bold text-navy-900 mb-4" data-id="element-367">
            Seeking Executive, Board &amp; Advisory Leadership
          </h2>
          <p className="text-lg text-slate-600 mb-8" data-id="element-368">
            Michelle welcomes conversations with organizations, boards, and
            executive recruiters nationwide.
          </p>
          <Link to="/contact" className="inline-flex items-center bg-navy-900 hover:bg-navy-800 text-white px-8 py-4 font-medium transition-colors" data-id="element-369">
            Schedule a Conversation
            <ArrowRight className="w-4 h-4 ml-2" data-id="element-370" />
          </Link>
        </div>
      </section>
    </div>;
}